import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Plane, Users, MapPin, Calendar, Clock, Plus, 
  RefreshCw, ChevronLeft, ChevronRight, Home, Settings, 
  Edit, Trash2, Check, X, Search, GripVertical,
  TrendingUp, BarChart2, List, CalendarDays, FileText
} from "lucide-react";
import { toast } from "sonner";
import "./XkAiLegs.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/xkailegs`;

// Format time HH:MM from minutes
const formatMinutes = (mins) => {
  if (!mins && mins !== 0) return "-";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${m.toString().padStart(2, "0")}`;
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
};

// Get dates array for a week starting from a date
const getWeekDates = (startDate) => {
  const dates = [];
  const start = new Date(startDate);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(d.toISOString().split("T")[0]);
  }
  return dates;
};

// Status badge colors
const statusColors = {
  planned: "status-planned",
  ready: "status-ready",
  active: "status-active",
  completed: "status-completed",
  cancelled: "status-cancelled",
};

// Aircraft status colors
const aircraftStatusColors = {
  active: "bg-green-500/20 text-green-400",
  maintenance: "bg-yellow-500/20 text-yellow-400",
  aog: "bg-red-500/20 text-red-400",
  inactive: "bg-gray-500/20 text-gray-400",
};

// Mission colors
const missionColors = {
  "SPARROW3": "#00ff88",
  "OSPREY4": "#00aaff",
  "TRAINING": "#ffaa00",
  "MAINTENANCE": "#888888",
};

export default function XkAiLegs() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("schedule");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [flights, setFlights] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [crew, setCrew] = useState([]);
  const [missions, setMissions] = useState([]);
  const [airports, setAirports] = useState([]);
  
  // Schedule state
  const [scheduleStartDate, setScheduleStartDate] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Monday
    return new Date(today.setDate(diff)).toISOString().split("T")[0];
  });
  const [scheduleView, setScheduleView] = useState("aircraft"); // aircraft or crew
  
  // Filter states
  const [flightFilter, setFlightFilter] = useState({ status: "", aircraft: "", mission: "" });
  
  // Modal states
  const [showFlightModal, setShowFlightModal] = useState(false);
  const [editingFlight, setEditingFlight] = useState(null);
  const [showAircraftModal, setShowAircraftModal] = useState(false);
  const [editingAircraft, setEditingAircraft] = useState(null);
  const [showCrewModal, setShowCrewModal] = useState(false);
  const [editingCrew, setEditingCrew] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);

  // Week dates for schedule
  const weekDates = useMemo(() => getWeekDates(scheduleStartDate), [scheduleStartDate]);

  // Fetch all data
  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard`);
      setDashboard(res.data);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
    }
  }, []);

  const fetchFlights = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (flightFilter.status) params.append("status", flightFilter.status);
      if (flightFilter.aircraft) params.append("aircraft", flightFilter.aircraft);
      if (flightFilter.mission) params.append("mission", flightFilter.mission);
      params.append("limit", "500");
      
      const res = await axios.get(`${API}/flights?${params.toString()}`);
      setFlights(res.data);
    } catch (err) {
      console.error("Error fetching flights:", err);
    }
  }, [flightFilter]);

  const fetchAircraft = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/aircraft`);
      setAircraft(res.data);
    } catch (err) {
      console.error("Error fetching aircraft:", err);
    }
  }, []);

  const fetchCrew = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/crew`);
      setCrew(res.data);
    } catch (err) {
      console.error("Error fetching crew:", err);
    }
  }, []);

  const fetchMissions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/missions`);
      setMissions(res.data);
    } catch (err) {
      console.error("Error fetching missions:", err);
    }
  }, []);

  const fetchAirports = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/airports`);
      setAirports(res.data);
    } catch (err) {
      console.error("Error fetching airports:", err);
    }
  }, []);

  // Seed data
  const seedData = async () => {
    try {
      setRefreshing(true);
      const res = await axios.post(`${API}/seed`);
      toast.success(`Seeded: ${JSON.stringify(res.data.seeded)}`);
      await refreshAll();
    } catch (err) {
      toast.error("Failed to seed data");
    } finally {
      setRefreshing(false);
    }
  };

  // Refresh all
  const refreshAll = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchDashboard(),
        fetchFlights(),
        fetchAircraft(),
        fetchCrew(),
        fetchMissions(),
        fetchAirports()
      ]);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  useEffect(() => {
    fetchFlights();
  }, [fetchFlights]);

  // Delete handlers
  const deleteFlight = async (flightId) => {
    if (!window.confirm("Delete this flight?")) return;
    try {
      await axios.delete(`${API}/flights/${flightId}`);
      toast.success("Flight deleted");
      fetchFlights();
      fetchDashboard();
    } catch (err) {
      toast.error("Failed to delete flight");
    }
  };

  const deleteAircraft = async (registration) => {
    if (!window.confirm("Delete this aircraft?")) return;
    try {
      await axios.delete(`${API}/aircraft/${registration}`);
      toast.success("Aircraft deleted");
      fetchAircraft();
      fetchDashboard();
    } catch (err) {
      toast.error("Failed to delete aircraft");
    }
  };

  const deleteCrew = async (code) => {
    if (!window.confirm("Delete this crew member?")) return;
    try {
      await axios.delete(`${API}/crew/${code}`);
      toast.success("Crew member deleted");
      fetchCrew();
    } catch (err) {
      toast.error("Failed to delete crew member");
    }
  };

  // Navigate schedule week
  const prevWeek = () => {
    const d = new Date(scheduleStartDate);
    d.setDate(d.getDate() - 7);
    setScheduleStartDate(d.toISOString().split("T")[0]);
  };

  const nextWeek = () => {
    const d = new Date(scheduleStartDate);
    d.setDate(d.getDate() + 7);
    setScheduleStartDate(d.toISOString().split("T")[0]);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    setScheduleStartDate(new Date(today.setDate(diff)).toISOString().split("T")[0]);
  };

  // Open flight modal for a specific date/resource
  const openNewFlight = (date, resourceId) => {
    setSelectedDate(date);
    setSelectedResource(resourceId);
    setEditingFlight({
      date: date,
      aircraft_reg: scheduleView === "aircraft" ? resourceId : "",
      departure_icao: "EBOS",
      arrival_icao: "EBOS",
      crew: scheduleView === "crew" ? [{ crew_code: resourceId, role: "PIC" }] : []
    });
    setShowFlightModal(true);
  };

  // Flight Modal Component
  const FlightModal = () => {
    const [form, setForm] = useState(editingFlight || {
      date: selectedDate || new Date().toISOString().split("T")[0],
      aircraft_reg: selectedResource || "",
      mission_code: "",
      callsign: "",
      category: "ISR flight",
      departure_icao: "EBOS",
      arrival_icao: "EBOS",
      alternate_icao: "",
      planned_obt: "",
      planned_tot: "",
      planned_ldt: "",
      planned_ibt: "",
      actual_obt: "",
      actual_tot: "",
      actual_ldt: "",
      actual_ibt: "",
      crew: [],
      block_fuel: "",
      pax_count: 0,
      remarks: ""
    });
    
    const [crewInput, setCrewInput] = useState({ code: "", role: "PIC" });

    const addCrew = () => {
      if (crewInput.code && !form.crew.find(c => c.crew_code === crewInput.code)) {
        const crewMember = crew.find(c => c.code === crewInput.code);
        setForm({
          ...form,
          crew: [...form.crew, {
            crew_code: crewInput.code,
            crew_name: crewMember ? `${crewMember.first_name} ${crewMember.last_name}` : crewInput.code,
            role: crewInput.role
          }]
        });
        setCrewInput({ code: "", role: "PIC" });
      }
    };

    const removeCrew = (code) => {
      setForm({ ...form, crew: form.crew.filter(c => c.crew_code !== code) });
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        const payload = { ...form };
        // Clean empty strings
        Object.keys(payload).forEach(k => {
          if (payload[k] === "") payload[k] = null;
        });
        
        if (editingFlight?.flight_id) {
          await axios.put(`${API}/flights/${editingFlight.flight_id}`, payload);
          toast.success("Flight updated");
        } else {
          await axios.post(`${API}/flights`, payload);
          toast.success("Flight created");
        }
        setShowFlightModal(false);
        setEditingFlight(null);
        setSelectedDate(null);
        setSelectedResource(null);
        fetchFlights();
        fetchDashboard();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to save flight");
      }
    };

    return (
      <div className="modal-overlay" onClick={() => { setShowFlightModal(false); setEditingFlight(null); }}>
        <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editingFlight?.flight_id ? `Edit Flight ${editingFlight.flight_id}` : "New Flight"}</h2>
            <button onClick={() => { setShowFlightModal(false); setEditingFlight(null); }}><X size={20} /></button>
          </div>
          <form onSubmit={handleSubmit} className="modal-form">
            {/* Row 1: Date, Aircraft, Mission, Status */}
            <div className="form-row form-row-4">
              <label>
                <span>Date *</span>
                <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required />
              </label>
              <label>
                <span>Aircraft *</span>
                <select value={form.aircraft_reg} onChange={e => setForm({...form, aircraft_reg: e.target.value})} required>
                  <option value="">Select...</option>
                  {aircraft.map(ac => (
                    <option key={ac.registration} value={ac.registration}>{ac.registration}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Mission</span>
                <select value={form.mission_code || ""} onChange={e => setForm({...form, mission_code: e.target.value})}>
                  <option value="">None</option>
                  {missions.map(m => (
                    <option key={m.code} value={m.code}>{m.code}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Status</span>
                <select value={form.status || "planned"} onChange={e => setForm({...form, status: e.target.value})}>
                  <option value="planned">Planned</option>
                  <option value="ready">Ready</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </label>
            </div>

            {/* Row 2: Callsign, Category */}
            <div className="form-row">
              <label>
                <span>Callsign</span>
                <input type="text" value={form.callsign || ""} onChange={e => setForm({...form, callsign: e.target.value.toUpperCase()})} placeholder="e.g. SPARROW31" />
              </label>
              <label>
                <span>Category</span>
                <select value={form.category || "ISR flight"} onChange={e => setForm({...form, category: e.target.value})}>
                  <option value="ISR flight">ISR Flight</option>
                  <option value="Training">Training</option>
                  <option value="Positioning">Positioning</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Test flight">Test Flight</option>
                </select>
              </label>
            </div>

            {/* Row 3: Airports */}
            <div className="form-row form-row-3">
              <label>
                <span>Departure *</span>
                <input type="text" value={form.departure_icao} onChange={e => setForm({...form, departure_icao: e.target.value.toUpperCase()})} required maxLength={4} />
              </label>
              <label>
                <span>Arrival *</span>
                <input type="text" value={form.arrival_icao} onChange={e => setForm({...form, arrival_icao: e.target.value.toUpperCase()})} required maxLength={4} />
              </label>
              <label>
                <span>Alternate</span>
                <input type="text" value={form.alternate_icao || ""} onChange={e => setForm({...form, alternate_icao: e.target.value.toUpperCase()})} maxLength={4} />
              </label>
            </div>

            {/* Row 4: Planned Times */}
            <div className="form-section">
              <h4>Planned Times (UTC)</h4>
              <div className="form-row form-row-4">
                <label>
                  <span>Off Block</span>
                  <input type="time" value={form.planned_obt || ""} onChange={e => setForm({...form, planned_obt: e.target.value})} />
                </label>
                <label>
                  <span>Takeoff</span>
                  <input type="time" value={form.planned_tot || ""} onChange={e => setForm({...form, planned_tot: e.target.value})} />
                </label>
                <label>
                  <span>Landing</span>
                  <input type="time" value={form.planned_ldt || ""} onChange={e => setForm({...form, planned_ldt: e.target.value})} />
                </label>
                <label>
                  <span>In Block</span>
                  <input type="time" value={form.planned_ibt || ""} onChange={e => setForm({...form, planned_ibt: e.target.value})} />
                </label>
              </div>
            </div>

            {/* Row 5: Actual Times */}
            <div className="form-section">
              <h4>Actual Times (UTC)</h4>
              <div className="form-row form-row-4">
                <label>
                  <span>Off Block</span>
                  <input type="time" value={form.actual_obt || ""} onChange={e => setForm({...form, actual_obt: e.target.value})} />
                </label>
                <label>
                  <span>Takeoff</span>
                  <input type="time" value={form.actual_tot || ""} onChange={e => setForm({...form, actual_tot: e.target.value})} />
                </label>
                <label>
                  <span>Landing</span>
                  <input type="time" value={form.actual_ldt || ""} onChange={e => setForm({...form, actual_ldt: e.target.value})} />
                </label>
                <label>
                  <span>In Block</span>
                  <input type="time" value={form.actual_ibt || ""} onChange={e => setForm({...form, actual_ibt: e.target.value})} />
                </label>
              </div>
            </div>

            {/* Row 6: Crew */}
            <div className="form-section">
              <h4>Crew</h4>
              <div className="crew-input-row">
                <select value={crewInput.code} onChange={e => setCrewInput({...crewInput, code: e.target.value})}>
                  <option value="">Select crew...</option>
                  {crew.filter(c => !form.crew.find(fc => fc.crew_code === c.code)).map(c => (
                    <option key={c.code} value={c.code}>{c.code} - {c.first_name} {c.last_name}</option>
                  ))}
                </select>
                <select value={crewInput.role} onChange={e => setCrewInput({...crewInput, role: e.target.value})}>
                  <option value="PIC">PIC</option>
                  <option value="SIC">SIC</option>
                  <option value="Sensor Operator">Sensor Op</option>
                  <option value="Liaison Officer">Liaison</option>
                  <option value="Observer">Observer</option>
                </select>
                <button type="button" className="btn-secondary" onClick={addCrew}>Add</button>
              </div>
              <div className="crew-list">
                {form.crew.map(c => (
                  <div key={c.crew_code} className="crew-item">
                    <span className="crew-code">{c.crew_code}</span>
                    <span className="crew-role">{c.role}</span>
                    <button type="button" onClick={() => removeCrew(c.crew_code)}><X size={14} /></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 7: Fuel, Pax */}
            <div className="form-row">
              <label>
                <span>Block Fuel (gal)</span>
                <input type="number" value={form.block_fuel || ""} onChange={e => setForm({...form, block_fuel: e.target.value ? parseInt(e.target.value) : null})} />
              </label>
              <label>
                <span>Passengers</span>
                <input type="number" value={form.pax_count || 0} onChange={e => setForm({...form, pax_count: parseInt(e.target.value) || 0})} min={0} />
              </label>
            </div>

            {/* Remarks */}
            <label>
              <span>Remarks</span>
              <textarea value={form.remarks || ""} onChange={e => setForm({...form, remarks: e.target.value})} rows={2} />
            </label>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => { setShowFlightModal(false); setEditingFlight(null); }}>Cancel</button>
              <button type="submit" className="btn-primary">Save Flight</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Aircraft Modal
  const AircraftModal = () => {
    const [form, setForm] = useState(editingAircraft || {
      registration: "",
      callsign: "",
      model: "DA-62",
      icao_type: "DA62",
      manufacturer: "Diamond Aircraft",
      homebase: "EBOS",
      mtow: 2300,
      fuel_capacity: 100,
      ac_hours: 0,
      ac_cycles: 0,
      status: "active",
      notes: ""
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (editingAircraft?.registration) {
          await axios.put(`${API}/aircraft/${editingAircraft.registration}`, form);
          toast.success("Aircraft updated");
        } else {
          await axios.post(`${API}/aircraft`, form);
          toast.success("Aircraft created");
        }
        setShowAircraftModal(false);
        setEditingAircraft(null);
        fetchAircraft();
        fetchDashboard();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to save aircraft");
      }
    };

    return (
      <div className="modal-overlay" onClick={() => { setShowAircraftModal(false); setEditingAircraft(null); }}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editingAircraft ? "Edit Aircraft" : "New Aircraft"}</h2>
            <button onClick={() => { setShowAircraftModal(false); setEditingAircraft(null); }}><X size={20} /></button>
          </div>
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-row">
              <label>
                <span>Registration *</span>
                <input type="text" value={form.registration} onChange={e => setForm({...form, registration: e.target.value.toUpperCase()})} required disabled={!!editingAircraft} placeholder="OO-XXX" />
              </label>
              <label>
                <span>Callsign</span>
                <input type="text" value={form.callsign || ""} onChange={e => setForm({...form, callsign: e.target.value.toUpperCase()})} placeholder="SPARROW3" />
              </label>
            </div>
            <div className="form-row form-row-3">
              <label>
                <span>Model</span>
                <input type="text" value={form.model} onChange={e => setForm({...form, model: e.target.value})} />
              </label>
              <label>
                <span>ICAO Type</span>
                <input type="text" value={form.icao_type} onChange={e => setForm({...form, icao_type: e.target.value.toUpperCase()})} maxLength={4} />
              </label>
              <label>
                <span>Homebase</span>
                <input type="text" value={form.homebase} onChange={e => setForm({...form, homebase: e.target.value.toUpperCase()})} maxLength={4} />
              </label>
            </div>
            <div className="form-row form-row-4">
              <label>
                <span>MTOW (kg)</span>
                <input type="number" value={form.mtow} onChange={e => setForm({...form, mtow: parseInt(e.target.value) || 0})} />
              </label>
              <label>
                <span>Fuel Cap (gal)</span>
                <input type="number" value={form.fuel_capacity} onChange={e => setForm({...form, fuel_capacity: parseInt(e.target.value) || 0})} />
              </label>
              <label>
                <span>Hours</span>
                <input type="number" step="0.1" value={form.ac_hours || 0} onChange={e => setForm({...form, ac_hours: parseFloat(e.target.value) || 0})} />
              </label>
              <label>
                <span>Cycles</span>
                <input type="number" value={form.ac_cycles || 0} onChange={e => setForm({...form, ac_cycles: parseInt(e.target.value) || 0})} />
              </label>
            </div>
            <label>
              <span>Status</span>
              <select value={form.status || "active"} onChange={e => setForm({...form, status: e.target.value})}>
                <option value="active">Active</option>
                <option value="maintenance">Maintenance</option>
                <option value="aog">AOG</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <label>
              <span>Notes</span>
              <textarea value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} rows={2} />
            </label>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => { setShowAircraftModal(false); setEditingAircraft(null); }}>Cancel</button>
              <button type="submit" className="btn-primary">Save Aircraft</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Crew Modal
  const CrewModal = () => {
    const [form, setForm] = useState(editingCrew || {
      code: "",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      homebase: "EBOS",
      roles: [],
      qualifications: [],
      employee_number: "",
      notes: ""
    });
    const [roleInput, setRoleInput] = useState("");

    const addRole = () => {
      if (roleInput && !form.roles.includes(roleInput)) {
        setForm({...form, roles: [...form.roles, roleInput]});
        setRoleInput("");
      }
    };

    const removeRole = (role) => {
      setForm({...form, roles: form.roles.filter(r => r !== role)});
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (editingCrew?.code) {
          await axios.put(`${API}/crew/${editingCrew.code}`, form);
          toast.success("Crew member updated");
        } else {
          await axios.post(`${API}/crew`, form);
          toast.success("Crew member created");
        }
        setShowCrewModal(false);
        setEditingCrew(null);
        fetchCrew();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to save crew member");
      }
    };

    return (
      <div className="modal-overlay" onClick={() => { setShowCrewModal(false); setEditingCrew(null); }}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editingCrew ? "Edit Crew Member" : "New Crew Member"}</h2>
            <button onClick={() => { setShowCrewModal(false); setEditingCrew(null); }}><X size={20} /></button>
          </div>
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-row form-row-3">
              <label>
                <span>Code (3 letters) *</span>
                <input type="text" value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} required disabled={!!editingCrew} maxLength={3} placeholder="XDU" />
              </label>
              <label>
                <span>First Name *</span>
                <input type="text" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} required />
              </label>
              <label>
                <span>Last Name *</span>
                <input type="text" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} required />
              </label>
            </div>
            <div className="form-row">
              <label>
                <span>Email *</span>
                <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
              </label>
              <label>
                <span>Phone</span>
                <input type="tel" value={form.phone || ""} onChange={e => setForm({...form, phone: e.target.value})} />
              </label>
            </div>
            <div className="form-row">
              <label>
                <span>Homebase</span>
                <input type="text" value={form.homebase} onChange={e => setForm({...form, homebase: e.target.value.toUpperCase()})} maxLength={4} />
              </label>
              <label>
                <span>Employee #</span>
                <input type="text" value={form.employee_number || ""} onChange={e => setForm({...form, employee_number: e.target.value})} />
              </label>
            </div>
            <label>
              <span>Roles</span>
              <div className="roles-input">
                <select value={roleInput} onChange={e => setRoleInput(e.target.value)}>
                  <option value="">Add role...</option>
                  <option value="PIC">PIC</option>
                  <option value="SIC">SIC</option>
                  <option value="Instructor">Instructor</option>
                  <option value="Examiner">Examiner</option>
                  <option value="Sensor Operator">Sensor Operator</option>
                  <option value="Liaison Officer">Liaison Officer</option>
                  <option value="Observer">Observer</option>
                </select>
                <button type="button" className="btn-secondary" onClick={addRole}>Add</button>
              </div>
              <div className="roles-tags">
                {form.roles.map(role => (
                  <span key={role} className="role-tag">
                    {role}
                    <button type="button" onClick={() => removeRole(role)}><X size={12} /></button>
                  </span>
                ))}
              </div>
            </label>
            <label>
              <span>Notes</span>
              <textarea value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} rows={2} />
            </label>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => { setShowCrewModal(false); setEditingCrew(null); }}>Cancel</button>
              <button type="submit" className="btn-primary">Save Crew</button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Schedule Tab - Gantt-style calendar
  const ScheduleTab = () => {
    const resources = scheduleView === "aircraft" ? aircraft : crew;
    const today = new Date().toISOString().split("T")[0];

    // Get flights for a specific resource and date
    const getFlightsForCell = (resourceId, date) => {
      return flights.filter(f => {
        if (scheduleView === "aircraft") {
          return f.aircraft_reg === resourceId && f.date === date;
        } else {
          return f.crew?.some(c => c.crew_code === resourceId) && f.date === date;
        }
      });
    };

    return (
      <div className="schedule-container">
        {/* Schedule Header */}
        <div className="schedule-header">
          <div className="schedule-nav">
            <button onClick={prevWeek} className="btn-icon"><ChevronLeft size={18} /></button>
            <button onClick={goToToday} className="btn-secondary">Today</button>
            <button onClick={nextWeek} className="btn-icon"><ChevronRight size={18} /></button>
            <span className="schedule-range">
              {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
            </span>
          </div>
          <div className="schedule-view-toggle">
            <button 
              className={scheduleView === "aircraft" ? "active" : ""} 
              onClick={() => setScheduleView("aircraft")}
            >
              <Plane size={14} /> Aircraft
            </button>
            <button 
              className={scheduleView === "crew" ? "active" : ""} 
              onClick={() => setScheduleView("crew")}
            >
              <Users size={14} /> Crew
            </button>
          </div>
          <button className="btn-primary" onClick={() => setShowFlightModal(true)}>
            <Plus size={16} /> New Flight
          </button>
        </div>

        {/* Schedule Grid */}
        <div className="schedule-grid">
          {/* Header Row */}
          <div className="schedule-row header">
            <div className="schedule-cell resource-header">
              {scheduleView === "aircraft" ? "Aircraft" : "Crew"}
            </div>
            {weekDates.map(date => (
              <div 
                key={date} 
                className={`schedule-cell date-header ${date === today ? "today" : ""}`}
              >
                <span className="date-day">{new Date(date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short" })}</span>
                <span className="date-num">{new Date(date + "T00:00:00").getDate()}</span>
              </div>
            ))}
          </div>

          {/* Resource Rows */}
          {resources.map(resource => {
            const resourceId = scheduleView === "aircraft" ? resource.registration : resource.code;
            const resourceName = scheduleView === "aircraft" 
              ? `${resource.registration}` 
              : `${resource.code}`;
            const resourceSub = scheduleView === "aircraft"
              ? resource.callsign || ""
              : `${resource.first_name} ${resource.last_name?.[0] || ""}.`;

            return (
              <div key={resourceId} className="schedule-row">
                <div className="schedule-cell resource-cell">
                  <span className="resource-name">{resourceName}</span>
                  <span className="resource-sub">{resourceSub}</span>
                </div>
                {weekDates.map(date => {
                  const cellFlights = getFlightsForCell(resourceId, date);
                  const isToday = date === today;

                  return (
                    <div 
                      key={date} 
                      className={`schedule-cell day-cell ${isToday ? "today" : ""} ${cellFlights.length ? "has-flights" : ""}`}
                      onClick={() => cellFlights.length === 0 && openNewFlight(date, resourceId)}
                    >
                      {cellFlights.map(f => (
                        <div 
                          key={f.flight_id}
                          className={`flight-block ${f.status}`}
                          style={{ borderLeftColor: missionColors[f.mission_code] || "#666" }}
                          onClick={(e) => { e.stopPropagation(); setEditingFlight(f); setShowFlightModal(true); }}
                        >
                          <span className="flight-route">{f.departure_icao}→{f.arrival_icao}</span>
                          <span className="flight-time">{f.planned_obt || ""}</span>
                        </div>
                      ))}
                      {cellFlights.length === 0 && (
                        <div className="add-flight-hint">
                          <Plus size={14} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {resources.length === 0 && (
            <div className="schedule-empty">
              <p>No {scheduleView === "aircraft" ? "aircraft" : "crew members"} found.</p>
              <button className="btn-secondary" onClick={seedData}>Seed Demo Data</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Dashboard Tab
  const DashboardTab = () => (
    <div className="xk-dashboard">
      <div className="stats-grid">
        <div className="stat-card" onClick={() => setActiveTab("schedule")}>
          <div className="stat-icon flights"><Calendar size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{dashboard?.today_flight_count || 0}</span>
            <span className="stat-label">Today's Flights</span>
          </div>
        </div>
        <div className="stat-card" onClick={() => setActiveTab("aircraft")}>
          <div className="stat-icon aircraft"><Plane size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{dashboard?.stats?.total_aircraft || aircraft.length}</span>
            <span className="stat-label">Aircraft</span>
          </div>
        </div>
        <div className="stat-card" onClick={() => setActiveTab("crew")}>
          <div className="stat-icon crew"><Users size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{dashboard?.stats?.total_crew || crew.length}</span>
            <span className="stat-label">Crew Members</span>
          </div>
        </div>
        <div className="stat-card" onClick={() => setActiveTab("flights")}>
          <div className="stat-icon total"><BarChart2 size={24} /></div>
          <div className="stat-content">
            <span className="stat-value">{dashboard?.stats?.total_flights || flights.length}</span>
            <span className="stat-label">Total Flights</span>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-section">
          <h3><Calendar size={18} /> Today ({dashboard?.today || new Date().toISOString().split("T")[0]})</h3>
          {dashboard?.today_flights?.length > 0 ? (
            <div className="flight-list">
              {dashboard.today_flights.map(f => (
                <div key={f.flight_id} className="flight-item" onClick={() => { setEditingFlight(f); setShowFlightModal(true); }}>
                  <div className="flight-info">
                    <span className="flight-id">{f.flight_id}</span>
                    <span className="flight-aircraft">{f.aircraft_reg}</span>
                    <span className="flight-route">{f.departure_icao} → {f.arrival_icao}</span>
                  </div>
                  <div className="flight-meta">
                    <span className="flight-time">{f.planned_obt || "-"} - {f.planned_ibt || "-"}</span>
                    <span className={`flight-status ${statusColors[f.status]}`}>{f.status}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No flights today</p>
          )}
        </div>

        <div className="dashboard-section">
          <h3><Plane size={18} /> Fleet</h3>
          <div className="fleet-grid">
            {aircraft.map(ac => (
              <div key={ac.registration} className="fleet-item" onClick={() => { setEditingAircraft(ac); setShowAircraftModal(true); }}>
                <span className="fleet-reg">{ac.registration}</span>
                <span className="fleet-callsign">{ac.callsign || "-"}</span>
                <span className={`fleet-status ${aircraftStatusColors[ac.status]}`}>{ac.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Flights Tab
  const FlightsTab = () => (
    <div className="xk-flights">
      <div className="tab-header">
        <div className="filter-bar">
          <select value={flightFilter.status} onChange={e => setFlightFilter({...flightFilter, status: e.target.value})}>
            <option value="">All Status</option>
            <option value="planned">Planned</option>
            <option value="ready">Ready</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={flightFilter.aircraft} onChange={e => setFlightFilter({...flightFilter, aircraft: e.target.value})}>
            <option value="">All Aircraft</option>
            {aircraft.map(ac => <option key={ac.registration} value={ac.registration}>{ac.registration}</option>)}
          </select>
          <select value={flightFilter.mission} onChange={e => setFlightFilter({...flightFilter, mission: e.target.value})}>
            <option value="">All Missions</option>
            {missions.map(m => <option key={m.code} value={m.code}>{m.code}</option>)}
          </select>
        </div>
        <button className="btn-primary" onClick={() => setShowFlightModal(true)}><Plus size={16} /> New Flight</button>
      </div>
      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Date</th>
              <th>Aircraft</th>
              <th>Mission</th>
              <th>Route</th>
              <th>Plan OBT/IBT</th>
              <th>Block/Flight</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {flights.map(f => (
              <tr key={f.flight_id} onClick={() => { setEditingFlight(f); setShowFlightModal(true); }}>
                <td className="cell-id">{f.flight_id}</td>
                <td>{formatDate(f.date)}</td>
                <td className="cell-aircraft">{f.aircraft_reg}</td>
                <td>{f.mission_code || "-"}</td>
                <td>{f.departure_icao} → {f.arrival_icao}</td>
                <td className="cell-times">{f.planned_obt || "-"} / {f.planned_ibt || "-"}</td>
                <td>{formatMinutes(f.planned_block_time)} / {formatMinutes(f.planned_flight_time)}</td>
                <td><span className={`status-badge ${statusColors[f.status]}`}>{f.status}</span></td>
                <td className="cell-actions" onClick={e => e.stopPropagation()}>
                  <button onClick={() => deleteFlight(f.flight_id)} className="btn-icon btn-danger"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {flights.length === 0 && <p className="no-data">No flights found</p>}
      </div>
    </div>
  );

  // Aircraft Tab
  const AircraftTab = () => (
    <div className="xk-aircraft">
      <div className="tab-header">
        <h3><Plane size={18} /> Fleet</h3>
        <button className="btn-primary" onClick={() => setShowAircraftModal(true)}><Plus size={16} /> Add Aircraft</button>
      </div>
      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Reg</th>
              <th>Callsign</th>
              <th>Type</th>
              <th>Homebase</th>
              <th>Hours</th>
              <th>Cycles</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {aircraft.map(ac => (
              <tr key={ac.registration} onClick={() => { setEditingAircraft(ac); setShowAircraftModal(true); }}>
                <td className="cell-reg">{ac.registration}</td>
                <td>{ac.callsign || "-"}</td>
                <td>{ac.model}</td>
                <td>{ac.homebase}</td>
                <td>{(ac.ac_hours || 0).toFixed(1)}</td>
                <td>{ac.ac_cycles || 0}</td>
                <td><span className={`status-badge ${aircraftStatusColors[ac.status]}`}>{ac.status}</span></td>
                <td className="cell-actions" onClick={e => e.stopPropagation()}>
                  <button onClick={() => deleteAircraft(ac.registration)} className="btn-icon btn-danger"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {aircraft.length === 0 && <p className="no-data">No aircraft</p>}
      </div>
    </div>
  );

  // Crew Tab
  const CrewTab = () => (
    <div className="xk-crew">
      <div className="tab-header">
        <h3><Users size={18} /> Crew</h3>
        <button className="btn-primary" onClick={() => setShowCrewModal(true)}><Plus size={16} /> Add Crew</button>
      </div>
      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Name</th>
              <th>Email</th>
              <th>Base</th>
              <th>Roles</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {crew.map(c => (
              <tr key={c.code} onClick={() => { setEditingCrew(c); setShowCrewModal(true); }}>
                <td className="cell-code">{c.code}</td>
                <td>{c.first_name} {c.last_name}</td>
                <td className="cell-email">{c.email}</td>
                <td>{c.homebase}</td>
                <td className="cell-roles">{c.roles?.map(r => <span key={r} className="role-tag small">{r}</span>)}</td>
                <td><span className={`status-badge ${c.status === "active" ? "status-active" : "status-completed"}`}>{c.status}</span></td>
                <td className="cell-actions" onClick={e => e.stopPropagation()}>
                  <button onClick={() => deleteCrew(c.code)} className="btn-icon btn-danger"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {crew.length === 0 && <p className="no-data">No crew members</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="xkailegs-page loading">
        <div className="spinner"></div>
        <p>Loading XkAiLegs...</p>
      </div>
    );
  }

  return (
    <div className="xkailegs-page" data-testid="xkailegs-page">
      <header className="xk-header">
        <div className="header-left">
          <button className="btn-icon" onClick={() => navigate("/")}><ChevronLeft size={20} /></button>
          <h1>XkAiLegs</h1>
        </div>
        <div className="header-right">
          <button className="btn-icon" onClick={refreshAll} disabled={refreshing}><RefreshCw size={18} className={refreshing ? "spin" : ""} /></button>
          <button className="btn-secondary btn-sm" onClick={seedData}><Settings size={14} /> Seed</button>
        </div>
      </header>

      <nav className="xk-tabs">
        <button className={activeTab === "schedule" ? "active" : ""} onClick={() => setActiveTab("schedule")} data-testid="tab-schedule">
          <CalendarDays size={16} /> Schedule
        </button>
        <button className={activeTab === "dashboard" ? "active" : ""} onClick={() => setActiveTab("dashboard")} data-testid="tab-dashboard">
          <Home size={16} /> Dashboard
        </button>
        <button className={activeTab === "flights" ? "active" : ""} onClick={() => setActiveTab("flights")} data-testid="tab-flights">
          <List size={16} /> Flights
        </button>
        <button className={activeTab === "aircraft" ? "active" : ""} onClick={() => setActiveTab("aircraft")} data-testid="tab-aircraft">
          <Plane size={16} /> Aircraft
        </button>
        <button className={activeTab === "crew" ? "active" : ""} onClick={() => setActiveTab("crew")} data-testid="tab-crew">
          <Users size={16} /> Crew
        </button>
      </nav>

      <main className="xk-content">
        {activeTab === "schedule" && <ScheduleTab />}
        {activeTab === "dashboard" && <DashboardTab />}
        {activeTab === "flights" && <FlightsTab />}
        {activeTab === "aircraft" && <AircraftTab />}
        {activeTab === "crew" && <CrewTab />}
      </main>

      {showFlightModal && <FlightModal />}
      {showAircraftModal && <AircraftModal />}
      {showCrewModal && <CrewModal />}
    </div>
  );
}
